import React, { useState } from 'react';
import { TextField, Button, Link, Typography, Container, Card, CardContent, Alert, Box } from '@mui/material';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrg } from '../context/OrgContext';

// Mirrors the server-side rules in authController.validatePassword
const validatePassword = (password) => {
  if (!password) return 'Password is required';
  if (password.length < 9) return 'Password must be at least 9 characters long';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
  if (!/[^A-Za-z0-9]/.test(password)) return 'Password must contain at least one special character';
  return null;
};

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { name: companyName } = useOrg();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/reset-password`, {
        token,
        password
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="xs">
      <Card sx={{ mt: 8, p: 2 }}>
        <CardContent>
          <Typography variant="h4" align="center">
            {companyName}
          </Typography>
          <Typography variant="h6" align="center" gutterBottom>
            Reset Password
          </Typography>

          {success ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              Your password has been reset. Taking you to the login page...
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Choose a new password: at least 9 characters with an uppercase
                letter, lowercase letter, number, and special character.
              </Typography>
              <TextField
                fullWidth
                label="New Password"
                name="password"
                type="password"
                margin="normal"
                variant="outlined"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                name="confirmPassword"
                type="password"
                margin="normal"
                variant="outlined"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              {error && (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              )}
              <Button
                fullWidth
                variant="contained"
                color="primary"
                type="submit"
                sx={{ mt: 2 }}
                disabled={isLoading}
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
              <Box mt={2} textAlign="center">
                <Link href="/forgot-password" variant="body2">
                  Request a new link
                </Link>
              </Box>
            </form>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default ResetPassword;
